<template>
    <div>
        <button
            name="change_address"
            class="w-full inline-flex justify-center items-center px-6 py-3 my-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            @click="open = true"
        >
            <PencilAltIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            Change Address
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg mx-4 mt-5 mb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:m-6"
                        >
                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="flex justify-between items-center px-4 py-5 sm:px-6 bg-gray-100 text-gray-800 rounded-t-lg"
                                >
                                    <div class="flex items-center">
                                        <TruckIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        <h3 class="font-bold">
                                            Shipping Address
                                        </h3>
                                    </div>

                                    <button
                                        type="button"
                                        class="text-gray-400 hover:text-gray-500 focus:outline-none"
                                        @click="open = false"
                                    >
                                        <span class="sr-only">Close</span>
                                        <XIcon
                                            class="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>

                                <div class="px-4 pt-5 pb-4 sm:p-6">
                                    <input
                                        type="hidden"
                                        name="_method"
                                        value="PATCH"
                                    />
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />
                                    <input
                                        type="hidden"
                                        name="shipping_address_id"
                                        :value="inputs.shipping_address_id"
                                    />

                                    <RadioGroup
                                        v-model="inputs.shipping_address_id"
                                    >
                                        <RadioGroupLabel class="sr-only">
                                            Address
                                        </RadioGroupLabel>
                                        <div
                                            class="bg-white rounded-md -space-y-px"
                                        >
                                            <RadioGroupOption
                                                :data-test="
                                                    'address-' + address.id
                                                "
                                                as="template"
                                                v-for="(
                                                    address, addressId
                                                ) in addresses"
                                                :key="address.id"
                                                :value="address.id"
                                                v-slot="{ checked }"
                                            >
                                                <div
                                                    :class="[
                                                        addressId === 0
                                                            ? 'rounded-tl-md rounded-tr-md'
                                                            : '',
                                                        checked
                                                            ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                                            : 'border-gray-200',
                                                        'relative border p-4 flex cursor-pointer focus:outline-none',
                                                    ]"
                                                >
                                                    <div
                                                        class="flex items-center"
                                                    >
                                                        <span
                                                            :class="[
                                                                checked
                                                                    ? 'bg-nutreance-green border-transparent'
                                                                    : 'bg-white border-gray-300',
                                                                'h-4 w-4 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                                            ]"
                                                            aria-hidden="true"
                                                        >
                                                            <span
                                                                class="rounded-full bg-white w-1.5 h-1.5"
                                                            />
                                                        </span>
                                                    </div>

                                                    <div
                                                        class="ml-3 flex flex-col"
                                                    >
                                                        <RadioGroupLabel
                                                            as="span"
                                                            :class="[
                                                                checked
                                                                    ? 'text-emerald-900'
                                                                    : 'text-gray-900',
                                                                'block text-sm font-medium',
                                                            ]"
                                                        >
                                                            {{ address.name }}
                                                        </RadioGroupLabel>
                                                        <RadioGroupDescription
                                                            as="span"
                                                            :class="[
                                                                checked
                                                                    ? 'text-emerald-700'
                                                                    : 'text-gray-500',
                                                                'block text-sm',
                                                            ]"
                                                        >
                                                            {{
                                                                address.line1 +
                                                                (address.line2
                                                                    ? " " +
                                                                      address.line2
                                                                    : "")
                                                            }},
                                                            {{ address.city }},
                                                            {{ address.state }}
                                                            {{
                                                                address.postal_code
                                                            }},
                                                            {{
                                                                address.country_code
                                                            }}
                                                        </RadioGroupDescription>
                                                    </div>
                                                </div>
                                            </RadioGroupOption>

                                            <RadioGroupOption
                                                id="use-new-address"
                                                as="template"
                                                value=""
                                                v-slot="{ checked }"
                                            >
                                                <div
                                                    :class="[
                                                        addresses.length === 0
                                                            ? 'rounded-tl-md rounded-tr-md'
                                                            : '',
                                                        checked
                                                            ? 'bg-emerald-50 border-nutreance-green-lighter z-10'
                                                            : 'border-gray-200',
                                                        'rounded-bl-md rounded-br-md relative border py-6 px-4 flex cursor-pointer focus:outline-none',
                                                    ]"
                                                >
                                                    <span
                                                        :class="[
                                                            checked
                                                                ? 'bg-nutreance-green border-transparent'
                                                                : 'bg-white border-gray-300',
                                                            'h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center shrink-0',
                                                        ]"
                                                        aria-hidden="true"
                                                    >
                                                        <span
                                                            class="rounded-full bg-white w-1.5 h-1.5"
                                                        />
                                                    </span>

                                                    <div
                                                        class="ml-3 flex flex-col"
                                                    >
                                                        <RadioGroupLabel
                                                            as="span"
                                                            :class="[
                                                                checked
                                                                    ? 'text-emerald-900'
                                                                    : 'text-gray-900',
                                                                'block text-sm font-medium',
                                                            ]"
                                                        >
                                                            Use a New Address
                                                        </RadioGroupLabel>
                                                    </div>
                                                </div>
                                            </RadioGroupOption>
                                        </div>
                                    </RadioGroup>
                                </div>

                                <div
                                    v-if="!inputs.shipping_address_id"
                                    class="px-4 pb-6 sm:px-6"
                                >
                                    <AddressFormFields
                                        :address="order.shipping_address"
                                        :countries="countries"
                                        :emailOptIn="emailOptIn"
                                        :errors="errors"
                                        :includeDefaultField="false"
                                        :includeVerifyField="
                                            !!canCreateUnverifiedAddress
                                        "
                                        :initialCountry="initialCountry"
                                        :initialStates="initialStates"
                                        :old="old"
                                        :type="'edit'"
                                    />
                                </div>

                                <div
                                    class="px-4 pb-4 sm:px-6 sm:pb-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Save
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    PencilAltIcon,
    TruckIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import AddressFormFields from "../addresses/AddressFormFields.vue";
import LoadingIcon from "../LoadingIcon.vue";

const props = withDefaults(
    defineProps<{
        addresses?: any;
        canCreateUnverifiedAddress?: string;
        countries?: any;
        csrf?: string;
        emailOptIn?: string;
        errors?: any;
        initialCountry?: any;
        initialStates?: any;
        old?: any;
        order?: any;
    }>(),
    {
        emailOptIn: "1",
    },
);

const { oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const actionUrl =
    "/user/orders/" + props.order.public_order_id + "/shipping-address";
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, "shipping_address_id"),
);

const inputs = reactive<{ shipping_address_id: number | string }>({
    shipping_address_id: Object.prototype.hasOwnProperty.call(
        props.old,
        "shipping_address_id",
    )
        ? oldInput(props.old, "shipping_address_id")
            ? parseInt(oldInput(props.old, "shipping_address_id"))
            : ""
        : props.order.shipping_address_id
          ? parseInt(props.order.shipping_address_id)
          : "",
});
</script>
